import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DeleteButton from "../components/DeleteButton";
import DismissibleAlert from "../components/DismissibleAlert";
import EditButton from "../components/EditButton";
import ProductDeleteConfirmationDialog from "../components/ProductDeleteConfirmationDialog";
import RequiredFieldMarker from "../components/RequiredFieldMarker";
import ProductVisibilityChip from "../components/ProductVisibilityChip";
import {
  ProductCatalogActionButton,
  ProductCatalogPage,
  ProductCatalogSectionHeader,
  ProductCatalogTableFrame,
  ProductCatalogToolbar,
  productCatalogFlexibleTableClassName,
} from "../components/ProductCatalogOverview";
import ProductImageManager, {
  ProductImagePreview,
} from "../components/ProductImageManager";
import ProductCatalogDialog from "../components/ProductCatalogDialog";
import ProductCatalogColumnFilter from "../components/ProductCatalogColumnFilter";
import ProductCatalogTableControls from "../components/ProductCatalogTableControls";
import ProductCatalogSortableHeader from "../components/ProductCatalogSortableHeader";
import ProductChipListField from "../components/ProductChipListField";
import ProductCatalogEmptyRow from "../components/ProductCatalogEmptyRow";
import { useAuth } from "../auth/AuthContext";
import {
  hasAnyPermission,
  hasPermission,
  isRootUser,
} from "../auth/permissions";
import { PRODUCT_VISIBILITY_OPTIONS } from "../constants/productVisibility";
import { readProductCatalogTableSettings } from "../utils/productCatalogTableSettings";
import {
  createFieldCase,
  deleteFieldCase,
  deleteFieldCaseImage,
  getFieldCases,
  importFieldCases,
  reorderFieldCaseImages,
  updateFieldCase,
  updateFieldCaseVisibility,
  uploadFieldCaseImage,
} from "../api/fieldCasesApi";

const PRODUCT_PERMISSIONS = [
  "FIELD_CASES_READ",
  "FIELD_CASES_CREATE",
  "FIELD_CASES_UPDATE",
  "FIELD_CASES_DELETE",
];
const EMPTY_FORM = {
  model: "",
  voltage: "12",
  power: "1.2",
  oems: [],
  manufacturer: "",
  applications: [],
  wai: "",
  asNumber: "",
  overallLength: "",
  outerDiameter: "",
  innerDiameter: "",
  visibility: "DRAFT",
};
const FIELDS = [
  ["model", "Model", "text", true],
  ["voltage", "Voltage", "select", true],
  ["power", "Power (kW)", "number", true],
  ["manufacturer", "Manufacturer", "text", true],
  ["wai", "WAI", "text", false],
  ["asNumber", "AS number", "text", false],
  ["overallLength", "Overall length", "number", false],
  ["outerDiameter", "Outer diameter", "number", false],
  ["innerDiameter", "Inner diameter", "number", false],
];
const FIELD_CASE_COLUMNS = [
  { key: "Model", label: "Model", getValue: (item) => item.model },
  { key: "Voltage", label: "Voltage", getValue: (item) => item.voltage },
  { key: "Power", label: "Power", getValue: (item) => item.power },
  {
    key: "Manufacturer",
    label: "Manufacturer",
    getValue: (item) => item.manufacturer,
  },
  { key: "OEM", label: "OEM", getValue: (item) => item.oems || [] },
  { key: "WAI", label: "WAI", getValue: (item) => item.wai },
  { key: "AS", label: "AS", getValue: (item) => item.asNumber },
  {
    key: "Applications",
    label: "Applications",
    getValue: (item) => item.applications || [],
  },
  {
    key: "Dimensions",
    label: "Dimensions",
    getValue: (item) =>
      [item.overallLength, item.outerDiameter, item.innerDiameter]
        .filter((value) => value != null)
        .join(" "),
  },
  {
    key: "Images",
    label: "Images",
    getValue: (item) => item.images?.length || 0,
  },
];
const FIELD_CASE_COLUMN_WIDTHS = {
  Model: "min-w-28",
  Voltage: "min-w-16",
  Power: "min-w-16",
  Manufacturer: "min-w-28",
  OEM: "min-w-28",
  WAI: "min-w-20",
  AS: "min-w-24",
  Applications: "min-w-44",
  Dimensions: "min-w-36",
  Images: "min-w-20",
};
const FIELD_CASE_TABLE_SETTINGS_KEY = "field-case-table-settings";
const readFieldCaseTableSettings = () =>
  readProductCatalogTableSettings(
    FIELD_CASE_TABLE_SETTINGS_KEY,
    FIELD_CASE_COLUMNS,
  );
const numberOrNull = (value) => (value === "" ? null : Number(value));
const createDraftId = () =>
  window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;

export function requestFromFieldCaseForm(form) {
  return {
    model: form.model.trim(),
    voltage: form.voltage,
    power: String(form.power).trim(),
    oems: form.oems.map((entry) => entry.trim()).filter(Boolean),
    manufacturer: form.manufacturer.trim(),
    applications: form.applications
      .map((entry) => entry.trim())
      .filter(Boolean),
    wai: form.wai.trim() || null,
    asNumber: form.asNumber.trim() || null,
    overallLength: numberOrNull(form.overallLength),
    outerDiameter: numberOrNull(form.outerDiameter),
    innerDiameter: numberOrNull(form.innerDiameter),
  };
}

export function requestFromFieldCaseImport(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) {
    throw new Error("Each imported field case must be a JSON object.");
  }
  const read = (key, legacy) => item[key] ?? item[legacy];
  const list = (value) =>
    Array.isArray(value) ? value.map(String) : value ? [String(value)] : [];
  const number = (value, field) => {
    if (value === undefined || value === null || value === "") return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) throw new Error(`${field} must be a number.`);
    return parsed;
  };
  return {
    model: String(read("model", "Model") ?? ""),
    voltage: String(read("voltage", "Voltage") ?? ""),
    power: String(read("power", "Power") ?? ""),
    oems: list(read("oems", "OEM")),
    manufacturer: String(read("manufacturer", "Manufacturer") ?? ""),
    applications: list(read("applications", "Application")),
    wai: read("wai", "WAI") || null,
    asNumber: read("asNumber", "AS") || null,
    overallLength: number(read("overallLength", "Length"), "Overall length"),
    outerDiameter: number(read("outerDiameter", "OD"), "Outer diameter"),
    innerDiameter: number(read("innerDiameter", "ID"), "Inner diameter"),
  };
}

function FieldCaseForm({
  initial,
  isEditing,
  canManageVisibility,
  onCancel,
  onSave,
  saving,
}) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [images, setImages] = useState(initial?.images || []);
  const [selectedImages, setSelectedImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [imageOrder, setImageOrder] = useState(
    (initial?.images || []).map((image) => image.imageId),
  );
  const [imageWarning, setImageWarning] = useState("");
  const [error, setError] = useState("");
  const update = (event) =>
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  const visibleImages = images.filter(
    (image) => !removedImageIds.includes(image.imageId),
  );
  const imageItems = [
    ...visibleImages.map((image) => ({
      key: image.imageId,
      name: image.imageName,
      src: image.imageUrl,
      alt: `Field case ${form.model}`,
      status: "Saved",
      source: image,
    })),
    ...selectedImages.map((image) => ({
      key: image.id,
      name: image.file.name,
      src: image.preview,
      alt: `Field case ${form.model}`,
      status: image.imageId ? "Uploaded" : "Ready to upload",
      source: image,
    })),
  ].sort(
    (first, second) =>
      imageOrder.indexOf(first.key) - imageOrder.indexOf(second.key),
  );

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    try {
      await onSave(
        requestFromFieldCaseForm(form),
        form.visibility,
        { images, selectedImages, removedImageIds, imageOrder },
        {
          onRecordSaved: (record) => setImages(record.images || []),
          onImageUploaded: (draftId, record, uploadedImage) => {
            setImages(record.images || []);
            setSelectedImages((current) =>
              current.map((draft) =>
                draft.id === draftId
                  ? { ...draft, imageId: uploadedImage.imageId }
                  : draft,
              ),
            );
          },
        },
      );
    } catch (saveError) {
      setError(saveError.message || "Could not save this field case.");
    }
  };

  const handleFiles = (event) => {
    const files = Array.from(event.target.files || []);
    const available = Math.max(
      0,
      5 - visibleImages.length - selectedImages.length,
    );
    const accepted = files
      .slice(0, available)
      .filter(
        (file) => file.type === "image/png" && file.size <= 5 * 1024 * 1024,
      );
    const drafts = accepted.map((file) => ({
      id: createDraftId(),
      file,
      preview: URL.createObjectURL(file),
    }));
    setSelectedImages((current) => [...current, ...drafts]);
    setImageOrder((current) => [
      ...current,
      ...drafts.map((image) => image.id),
    ]);
    setImageWarning(
      accepted.length < files.length
        ? "Only PNG images up to 5 MB each are supported, with up to five images total."
        : "",
    );
    event.target.value = "";
  };

  const removeImage = (entry) => {
    const image = entry.source;
    if (entry.key === image.imageId) {
      setRemovedImageIds((current) => [...current, image.imageId]);
    } else {
      URL.revokeObjectURL(image.preview);
      setSelectedImages((current) =>
        current.filter((draft) => draft.id !== image.id),
      );
    }
    setImageOrder((current) => current.filter((key) => key !== entry.key));
  };

  return (
    <ProductCatalogDialog
      productName="Field case"
      title={isEditing ? `Edit field case ${form.model}` : "Create field case"}
      visibility={form.visibility}
      onClose={onCancel}
      closeDisabled={saving}
    >
      {error && (
        <DismissibleAlert onDismiss={() => setError("")}>
          {error}
        </DismissibleAlert>
      )}
      <form onSubmit={submit} className="space-y-4 p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {FIELDS.map(([name, label, type, required]) => (
            <label
              key={name}
              className="block text-sm font-medium text-zinc-700"
            >
              {label}
              {required && <RequiredFieldMarker />}
              {type === "select" ? (
                <select
                  name={name}
                  value={form[name]}
                  onChange={update}
                  required={required}
                  className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 font-normal"
                >
                  <option value="12">12 V</option>
                  <option value="24">24 V</option>
                </select>
              ) : (
                <input
                  name={name}
                  value={form[name]}
                  onChange={update}
                  required={required}
                  type={type}
                  min={type === "number" ? "0.1" : undefined}
                  step={type === "number" ? "0.1" : undefined}
                  maxLength={name === "model" ? 100 : undefined}
                  className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 font-normal"
                />
              )}
            </label>
          ))}
          {canManageVisibility && (
            <label className="block text-sm font-medium text-zinc-700">
              Visibility
              <select
                name="visibility"
                value={form.visibility}
                onChange={update}
                className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 font-normal"
              >
                {PRODUCT_VISIBILITY_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {[
          ["oems", "OEM numbers", "Type an OEM number and press Enter"],
          [
            "applications",
            "Applications",
            "Type an application and press Enter",
          ],
        ].map(([name, label, placeholder]) => (
          <ProductChipListField
            key={name}
            id={`field-case-${name}`}
            label={label}
            placeholder={placeholder}
            values={form[name]}
            onChange={(values) =>
              setForm((current) => ({ ...current, [name]: values }))
            }
          />
        ))}
        <ProductImageManager
          id="field-case-image"
          images={imageItems}
          onFilesSelected={handleFiles}
          onMove={(index, offset) =>
            setImageOrder((current) => {
              const next = [...current];
              const source = next.indexOf(imageItems[index].key);
              const target = source + offset;
              if (target >= 0 && target < next.length)
                [next[source], next[target]] = [next[target], next[source]];
              return next;
            })
          }
          onRemove={removeImage}
          disabled={saving}
        />
        {imageWarning && (
          <p role="alert" className="text-xs text-amber-800">
            {imageWarning}
          </p>
        )}
        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save field case"}
          </button>
        </div>
      </form>
    </ProductCatalogDialog>
  );
}

function FieldCaseCatalogCell({
  item,
  columnKey,
  mayChangeVisibility,
  onChangeVisibility,
}) {
  if (columnKey === "Model") {
    return (
      <div className="font-medium">
        {item.model}
        <ProductVisibilityChip
          item={item}
          canChangeVisibility={mayChangeVisibility}
          onChange={onChangeVisibility}
        />
      </div>
    );
  }
  if (columnKey === "Voltage") return `${item.voltage} V`;
  if (columnKey === "Power") return `${item.power} kW`;
  if (columnKey === "Manufacturer") return item.manufacturer;
  if (columnKey === "OEM") return (item.oems || []).join(", ") || "—";
  if (columnKey === "WAI") return item.wai || "—";
  if (columnKey === "AS") return item.asNumber || "—";
  if (columnKey === "Applications") {
    return (
      <div className="max-w-64">
        {(item.applications || []).map((application) => (
          <div key={application}>{application}</div>
        ))}
      </div>
    );
  }
  if (columnKey === "Dimensions") {
    return (
      <div className="text-xs">
        {[
          ["Overall length", item.overallLength],
          ["Outer diameter", item.outerDiameter],
          ["Inner diameter", item.innerDiameter],
        ]
          .filter(([, value]) => value != null)
          .map(([label, value]) => (
            <div key={label}>
              {label}: {value}
            </div>
          ))}
      </div>
    );
  }
  if (columnKey === "Images") {
    return (
      <div className="flex gap-1">
        {(item.images || []).slice(0, 3).map((image) => (
          <ProductImagePreview
            key={image.imageId}
            src={image.imageUrl}
            alt={`Field case ${item.model}`}
          />
        ))}
        {!item.images?.length && "—"}
      </div>
    );
  }
  return null;
}

export default function FieldCasesPage() {
  const { user } = useAuth();
  const [savedTableSettings] = useState(readFieldCaseTableSettings);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visibilityFilter, setVisibilityFilter] = useState("ALL");
  const [columnFilters, setColumnFilters] = useState(
    savedTableSettings.columnFilters,
  );
  const [sortConfig, setSortConfig] = useState({
    ...savedTableSettings.sortConfig,
  });
  const [columnOrder, setColumnOrder] = useState(
    savedTableSettings.columnOrder,
  );
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(
    savedTableSettings.visibleColumnKeys,
  );
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const confirmDeleteRef = useRef(null);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [importing, setImporting] = useState(false);
  const fileInput = useRef(null);
  const canRead = hasPermission(user, "FIELD_CASES_READ");
  const canCreate = hasPermission(user, "FIELD_CASES_CREATE");
  const canUpdate = hasPermission(user, "FIELD_CASES_UPDATE");
  const canDelete = hasPermission(user, "FIELD_CASES_DELETE");
  const canManageVisibility = hasPermission(
    user,
    "FIELD_CASES_VISIBILITY_MANAGE",
  );

  const loadItems = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      setItems(await getFieldCases());
    } catch (requestError) {
      setLoadError(requestError.message || "Could not load field cases.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (canRead) loadItems();
    else setLoading(false);
  }, [canRead, loadItems]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        FIELD_CASE_TABLE_SETTINGS_KEY,
        JSON.stringify({
          columnOrder,
          visibleColumnKeys,
          columnFilters,
          sortConfig,
        }),
      );
    } catch {
      // Keep the table usable when browser storage is unavailable.
    }
  }, [columnFilters, columnOrder, sortConfig, visibleColumnKeys]);

  const visibleItems = useMemo(() => {
    const filteredItems = items.filter(
      (item) =>
        (visibilityFilter === "ALL" || item.visibility === visibilityFilter) &&
        FIELD_CASE_COLUMNS.every(({ key, getValue }) => {
          const filter = (columnFilters[key] || "").trim().toLowerCase();
          if (!filter) return true;
          const value = getValue(item);
          if (key === "Images") {
            return filter === "has-images" ? value > 0 : value === 0;
          }
          const searchableValue = Array.isArray(value)
            ? value.join(" ")
            : value;
          return String(searchableValue ?? "")
            .toLowerCase()
            .includes(filter);
        }),
    );

    const column = FIELD_CASE_COLUMNS.find(({ key }) => key === sortConfig.key);
    if (!column) return filteredItems;
    return [...filteredItems].sort((first, second) => {
      const text = (item) => {
        const value = column.getValue(item);
        return Array.isArray(value) ? value.join(" ") : String(value ?? "");
      };
      const comparison = text(first).localeCompare(text(second), undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [columnFilters, items, sortConfig, visibilityFilter]);

  const sortByColumn = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const orderedColumns = columnOrder
    .filter((key) => visibleColumnKeys.includes(key))
    .map((key) => FIELD_CASE_COLUMNS.find((column) => column.key === key));

  const reorderColumns = (event, targetKey) => {
    event.preventDefault();
    const sourceKey = event.dataTransfer.getData("text/plain");
    if (!sourceKey || sourceKey === targetKey) return;
    setColumnOrder((currentOrder) => {
      const nextOrder = [...currentOrder];
      const sourceIndex = nextOrder.indexOf(sourceKey);
      const targetIndex = nextOrder.indexOf(targetKey);
      if (sourceIndex < 0 || targetIndex < 0) return currentOrder;
      nextOrder.splice(sourceIndex, 1);
      nextOrder.splice(targetIndex, 0, sourceKey);
      return nextOrder;
    });
  };

  const saveItem = async (request, visibility, imageChanges, progress) => {
    setSaving(true);
    setError("");
    try {
      const isEditing = Boolean(editing?.item);
      let current = isEditing
        ? await updateFieldCase(editing.item.fieldCaseId, request)
        : await createFieldCase(request);
      const saveRecord = (record) => {
        current = record;
        setItems((previous) =>
          [
            ...previous.filter(
              (item) => item.fieldCaseId !== record.fieldCaseId,
            ),
            record,
          ].sort((a, b) => a.model.localeCompare(b.model)),
        );
        progress.onRecordSaved(record);
      };
      saveRecord(current);
      for (const imageId of imageChanges.removedImageIds) {
        if (current.images?.some((image) => image.imageId === imageId))
          saveRecord(await deleteFieldCaseImage(current.fieldCaseId, imageId));
      }
      const uploadedIdsByDraft = new Map();
      for (const draft of imageChanges.selectedImages) {
        if (
          draft.imageId &&
          current.images?.some((image) => image.imageId === draft.imageId)
        ) {
          uploadedIdsByDraft.set(draft.id, draft.imageId);
          continue;
        }
        const previousIds = new Set(
          (current.images || []).map((image) => image.imageId),
        );
        const updated = await uploadFieldCaseImage(
          current.fieldCaseId,
          draft.file,
        );
        const uploadedImage = updated.images?.find(
          (image) => !previousIds.has(image.imageId),
        );
        saveRecord(updated);
        if (uploadedImage) {
          uploadedIdsByDraft.set(draft.id, uploadedImage.imageId);
          progress.onImageUploaded(draft.id, updated, uploadedImage);
        }
      }
      const desired = imageChanges.imageOrder
        .map(
          (key) =>
            current.images?.find((image) => image.imageId === key)?.imageId ||
            uploadedIdsByDraft.get(key),
        )
        .filter(Boolean);
      const fullOrder = [
        ...desired,
        ...(current.images || [])
          .map((image) => image.imageId)
          .filter((id) => !desired.includes(id)),
      ];
      if (
        fullOrder.length > 1 &&
        fullOrder.some((id, index) => current.images[index]?.imageId !== id)
      )
        saveRecord(
          await reorderFieldCaseImages(current.fieldCaseId, fullOrder),
        );
      const originalVisibility =
        editing?.item?.visibility || current.visibility;
      if (visibility !== originalVisibility)
        saveRecord(
          await updateFieldCaseVisibility(current.fieldCaseId, visibility),
        );
      setNotice(`Field case ${isEditing ? "updated" : "created"}.`);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (item) =>
    setEditing({
      item,
      form: {
        ...EMPTY_FORM,
        ...item,
        oems: [...(item.oems || [])],
        applications: [...(item.applications || [])],
        overallLength: item.overallLength ?? "",
        outerDiameter: item.outerDiameter ?? "",
        innerDiameter: item.innerDiameter ?? "",
      },
    });
  const changeVisibility = async (item, visibility) => {
    try {
      const updated = await updateFieldCaseVisibility(
        item.fieldCaseId,
        visibility,
      );
      setItems((current) =>
        current.map((candidate) =>
          candidate.fieldCaseId === item.fieldCaseId ? updated : candidate,
        ),
      );
      setNotice(`Visibility changed to ${visibility}.`);
    } catch (requestError) {
      setError(requestError.message || "Could not change visibility.");
    }
  };
  const removeItem = (item) => {
    setDeleteError("");
    setPendingDelete(item);
  };

  const confirmDeleteItem = async () => {
    if (!pendingDelete || deletingItemId) return;
    setDeletingItemId(pendingDelete.fieldCaseId);
    setDeleteError("");
    try {
      await deleteFieldCase(pendingDelete.fieldCaseId);
      setItems((current) =>
        current.filter(
          (candidate) => candidate.fieldCaseId !== pendingDelete.fieldCaseId,
        ),
      );
      setPendingDelete(null);
      setNotice("Field case deleted.");
    } catch (requestError) {
      setDeleteError(requestError.message || "Could not delete field case.");
    } finally {
      setDeletingItemId(null);
    }
  };
  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const data = JSON.parse(await file.text());
      if (!Array.isArray(data))
        throw new Error("The import file must contain a JSON array.");
      const result = await importFieldCases(
        data.map(requestFromFieldCaseImport),
      );
      if (canRead) await loadItems();
      setNotice(
        `Import complete: ${result.created?.length ?? 0} added${
          result.skippedModels?.length
            ? `, ${result.skippedModels.length} existing models skipped`
            : ""
        }.`,
      );
    } catch (requestError) {
      setError(requestError.message || "Could not import field cases.");
    } finally {
      setImporting(false);
    }
  };
  const exportItems = () => {
    const blobUrl = URL.createObjectURL(
      new Blob(
        [
          JSON.stringify(
            items.map((item) => ({
              imageUrl: item.images?.[0]?.imageUrl || "",
              Model: item.model,
              Voltage: item.voltage,
              Power: item.power,
              OEM: item.oems || [],
              Manufacturer: item.manufacturer,
              WAI: item.wai || "",
              AS: item.asNumber || "",
              OD: item.outerDiameter,
              ID: item.innerDiameter,
              Length: item.overallLength,
              Application: item.applications || [],
            })),
            null,
            2,
          ),
        ],
        { type: "application/json" },
      ),
    );
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `field-cases_${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  };

  const canAccessCatalog = hasAnyPermission(user, PRODUCT_PERMISSIONS);
  if (!canAccessCatalog)
    return (
      <ProductCatalogPage
        title="Field Cases"
        description="Search, review, and maintain product specifications."
      >
        <p className="mt-3 text-zinc-600">
          You don’t have permission to view or manage field cases.
        </p>
      </ProductCatalogPage>
    );

  return (
    <ProductCatalogPage
      title="Field Cases"
      description="Search, review, and maintain field case specifications."
    >
      {(canRead || canCreate) && (
        <ProductCatalogToolbar>
          {canCreate && (
            <ProductCatalogActionButton
              variant="primary"
              label="Add field case"
              title="Add field case"
              onClick={() => setEditing({ item: null, form: EMPTY_FORM })}
              disabled={loading || Boolean(loadError) || saving}
              icon="/icons/create-icon.svg"
            >
              Add field case
            </ProductCatalogActionButton>
          )}
          {isRootUser(user) && canRead && (
            <ProductCatalogActionButton
              label="Download data"
              title="Download data"
              onClick={exportItems}
              disabled={!items.length}
              icon="/icons/download-icon.svg"
            >
              Export data
            </ProductCatalogActionButton>
          )}
          {isRootUser(user) && canCreate && (
            <>
              <ProductCatalogActionButton
                label="Import JSON"
                title="Import JSON"
                onClick={() => fileInput.current?.click()}
                disabled={importing || loading}
                icon="/icons/import-icon.svg"
              >
                {importing ? "Importing…" : "Import JSON"}
              </ProductCatalogActionButton>
              <input
                ref={fileInput}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={importFile}
              />
            </>
          )}
        </ProductCatalogToolbar>
      )}
      {error && (
        <DismissibleAlert onDismiss={() => setError("")}>
          {error}
        </DismissibleAlert>
      )}
      {notice && (
        <DismissibleAlert variant="success" onDismiss={() => setNotice("")}>
          {notice}
        </DismissibleAlert>
      )}
      {loadError && !loading && (
        <div role="alert" className="mb-4 rounded bg-red-100 p-3 text-red-800">
          <p>Could not load field cases: {loadError}</p>
          <button
            type="button"
            className="mt-2 rounded bg-red-800 px-3 py-1 text-white"
            onClick={loadItems}
          >
            Try again
          </button>
        </div>
      )}
      {canRead && !loadError && (
        <>
          <ProductCatalogSectionHeader
            title="Field case records"
            description="Filter in the column headers; select a header to sort."
            controls={
              <>
                <ProductCatalogTableControls
                  visibilityFilter={visibilityFilter}
                  onVisibilityFilterChange={setVisibilityFilter}
                  onReset={() => {
                    setColumnFilters({});
                    setVisibilityFilter("ALL");
                    setSortConfig({ key: "Model", direction: "asc" });
                  }}
                  columns={FIELD_CASE_COLUMNS}
                  visibleColumnKeys={visibleColumnKeys}
                  onVisibleColumnKeysChange={setVisibleColumnKeys}
                />
              </>
            }
          />
          {loading ? (
            <p className="py-8 text-center text-zinc-600" role="status">
              Loading field cases…
            </p>
          ) : (
            <ProductCatalogTableFrame label="Field case catalog">
              <table className={productCatalogFlexibleTableClassName}>
                <thead className="bg-gray-50">
                  <tr>
                    {orderedColumns.map(({ key, label }) => (
                      <ProductCatalogSortableHeader
                        key={key}
                        columnKey={key}
                        label={label}
                        sortConfig={sortConfig}
                        onSort={sortByColumn}
                        widthClass={FIELD_CASE_COLUMN_WIDTHS[key]}
                        onDropColumn={reorderColumns}
                        filterId={`field-case-filter-${key}`}
                      >
                        <ProductCatalogColumnFilter
                          id={`field-case-filter-${key}`}
                          label={label}
                          value={columnFilters[key] || ""}
                          onChange={(value) =>
                            setColumnFilters((current) => ({
                              ...current,
                              [key]: value,
                            }))
                          }
                          options={
                            key === "Images"
                              ? [
                                  { value: "has-images", label: "Has images" },
                                  { value: "no-images", label: "No images" },
                                ]
                              : undefined
                          }
                        />
                      </ProductCatalogSortableHeader>
                    ))}
                    <th
                      scope="col"
                      className="border-b border-gray-200 px-2 py-2 text-left align-top"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {visibleItems.length === 0 ? (
                    <ProductCatalogEmptyRow colSpan={orderedColumns.length + 1}>
                      {items.length
                        ? "No field cases match the current filters."
                        : "No field cases are available yet."}
                    </ProductCatalogEmptyRow>
                  ) : (
                    visibleItems.map((item) => {
                      const ownsItem = item.createdByUserId === user?.userId;
                      const mayChangeVisibility =
                        canManageVisibility || (ownsItem && canUpdate);
                      return (
                        <tr
                          key={item.fieldCaseId}
                          className="align-top hover:bg-gray-50"
                        >
                          {orderedColumns.map(({ key }) => (
                            <td
                              key={key}
                              className="break-words px-2 py-3 text-gray-800"
                            >
                              <FieldCaseCatalogCell
                                item={item}
                                columnKey={key}
                                mayChangeVisibility={mayChangeVisibility}
                                onChangeVisibility={changeVisibility}
                              />
                            </td>
                          ))}
                          <td className="px-2 py-3">
                            <div className="flex gap-1">
                              {canUpdate && (
                                <EditButton
                                  onClick={() => beginEdit(item)}
                                  label={`Edit field case ${item.model}`}
                                  title="Edit field case"
                                />
                              )}
                              {canDelete && (
                                <DeleteButton
                                  onClick={() => removeItem(item)}
                                  label={`Delete field case ${item.model}`}
                                  title="Delete field case"
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </ProductCatalogTableFrame>
          )}
        </>
      )}
      {editing && (
        <FieldCaseForm
          initial={{ ...editing.form, images: editing.item?.images || [] }}
          isEditing={Boolean(editing.item)}
          canManageVisibility={
            canManageVisibility ||
            (canUpdate &&
              (!editing.item || editing.item.createdByUserId === user?.userId))
          }
          onCancel={() => setEditing(null)}
          onSave={saveItem}
          saving={saving}
        />
      )}
      {pendingDelete && (
        <ProductDeleteConfirmationDialog
          productName="field case"
          model={pendingDelete.model}
          error={deleteError}
          deleting={deletingItemId !== null}
          onCancel={() => {
            setPendingDelete(null);
            setDeleteError("");
          }}
          onConfirm={confirmDeleteItem}
          confirmButtonRef={confirmDeleteRef}
        />
      )}
    </ProductCatalogPage>
  );
}
